import * as React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Dialog primitive — shared modal shell for the design-system layer
 * (`components/ui/` shadcn-style convention, see `docs/design/design-system.md`).
 *
 * Wraps `@radix-ui/react-dialog` — already installed — with design-system tokens
 * and Foundry GUI conventions:
 *
 *   - `app-region-no-drag` on every interactive element (Electron drag-region rule
 *     from design-system.md §4 / issues #531 / #804).
 *   - Overlay: `bg-black/60` scrim, `z-40`.
 *   - Content: `bg-crucible-surface` + `--shadow-modal` token, `rounded-[var(--radius-lg)]`,
 *     `z-50`, centered with CSS translate.
 *   - Standard parts: Dialog / DialogTrigger / DialogContent / DialogHeader /
 *     DialogFooter / DialogTitle / DialogDescription / DialogClose.
 *
 * `DesignModal` (pages/design/DesignModal.tsx) is refactored to render on top of
 * these parts — its visual output and behaviour are unchanged.
 *
 * Usage:
 *
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent>
 *       <DialogHeader>
 *         <DialogTitle>Title</DialogTitle>
 *         <DialogDescription>Optional description</DialogDescription>
 *       </DialogHeader>
 *       {children}
 *       <DialogFooter>
 *         <DialogClose asChild><Button variant="secondary">Cancel</Button></DialogClose>
 *       </DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 */

// ---------------------------------------------------------------------------
// Root + Trigger (thin re-exports — no visual change)
// ---------------------------------------------------------------------------

const Dialog = RadixDialog.Root;
Dialog.displayName = 'Dialog';

const DialogTrigger = RadixDialog.Trigger;
DialogTrigger.displayName = 'DialogTrigger';

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...props }, ref) => (
  <RadixDialog.Overlay
    ref={ref}
    className={cn(
      // `app-region-no-drag` — the overlay is the backdrop-dismiss target;
      // without it, clicks on the scrim get swallowed by the Electron
      // window drag-region (design-system §4 / issues #804 / #965).
      'app-region-no-drag',
      'fixed inset-0 z-40 bg-black/60',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  /** Show the built-in X close button in the top-right corner (default true). */
  showClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(({ className, children, showClose = true, ...props }, ref) => (
  <RadixDialog.Portal>
    <DialogOverlay />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        // `app-region-no-drag` — the modal panel sits at header-y over the
        // draggable window chrome; without it, clicks on the panel (when the
        // child isn't already a Button) get swallowed by the drag-region
        // (design-system §4 / issues #804 / #965).
        'app-region-no-drag',
        // Position
        'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
        // Sizing
        'w-[min(90vw,720px)] max-h-[85vh] overflow-y-auto',
        // Surface
        'rounded-[var(--radius-lg)] border border-crucible-border bg-crucible-surface p-5',
        'shadow-[var(--shadow-modal)]',
        // Entry/exit animations
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className,
      )}
      {...props}
    >
      {children}
      {showClose && (
        <RadixDialog.Close
          className={cn(
            'app-region-no-drag',
            'absolute right-4 top-4 rounded p-1',
            'text-crucible-muted opacity-70 transition-opacity',
            'hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-crucible-accent/60',
            'disabled:pointer-events-none',
          )}
          aria-label="Close"
        >
          <X size={15} />
        </RadixDialog.Close>
      )}
    </RadixDialog.Content>
  </RadixDialog.Portal>
));
DialogContent.displayName = 'DialogContent';

// ---------------------------------------------------------------------------
// Header / Footer
// ---------------------------------------------------------------------------

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-3 flex flex-col gap-1', className)} {...props} />;
}
DialogHeader.displayName = 'DialogHeader';

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-4 flex items-center justify-end gap-2', className)} {...props} />;
}
DialogFooter.displayName = 'DialogFooter';

// ---------------------------------------------------------------------------
// Title + Description
// ---------------------------------------------------------------------------

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn('text-sm font-semibold text-crucible-fg', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn('text-xs text-crucible-muted', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

// ---------------------------------------------------------------------------
// Close (re-export for asChild usage with other components)
// ---------------------------------------------------------------------------

const DialogClose = RadixDialog.Close;
DialogClose.displayName = 'DialogClose';

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Dialog,
  DialogTrigger,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
